// ─── Types ────────────────────────────────────────────────────────────────────

export type AdvisorForecast = {
  airTempC: number | null;
  pressureHpa: number | null;
  pressureTrend: string | null;
  windDirection: string | null;
  windSpeedMph: number | null;
  cloudPct: number | null;
  precipitationMm: number | null;
};

export type AdvisorInput = {
  fishingDate: string;
  expectedStartTime: string;
  expectedDurationHours: number;
  forecast: AdvisorForecast;
};

export type AdvisorSession = {
  /** Session/angler duration in hours — used for duration exposure weighting.
   *  Null = unknown duration; treated as neutral (neither rewarded nor penalised). */
  duration_hours?: number | string | null;
  /** For personal evidence this is the session_angler row id (one row per angler
   *  per session). For community evidence this is the physical session id. */
  id: string | number;
  /** Physical session id — used to join catches. For community rows this equals id. */
  session_id?: string | number;
  start_at: string;
  weather?: string | null;
  air_temp_c?: number | string | null;
  water_temp_c?: number | string | null;
  wind_direction?: string | null;
  wind_speed_mph?: number | string | null;
  air_pressure_hpa?: number | string | null;
  cloud_cover_pct?: number | string | null;
  rain_observed?: boolean | null;
  pressure_trend?: string | null;
  /** IANA timezone captured for the session location, e.g. Europe/Paris. */
  environment_timezone?: string | null;
  /** Number of catches of the species currently being advised on. */
  catch_count: number | string;
  /**
   * True when this session was actually targeting the advised species (or both).
   * False means the session only contributes positive caught-species pattern
   * evidence. It must never enter catch-rate, blank-rate, Fishing Score or
   * confidence denominators.
   */
  denominator_eligible?: boolean;
  /** "personal" = the requesting user's own record (one row per session-angler).
   *  "community" = another user's anonymous physical-session record. */
  source: "personal" | "community";
  peg?: string | null;
  /** Structured rod-setup exposure. Unlike catch fields, these can tell us what
   * was actually fished during a blank session, so they are valid contradiction evidence. */
  setup_rigs?: string[] | string | null;
  setup_main_baits?: string[] | string | null;
  setup_hookbaits?: string[] | string | null;
  setup_additives?: string[] | string | null;
  setup_bait_approaches?: string[] | string | null;
  /** Timed setup intervals used to scale contradiction evidence by actual time in use. */
  setup_exposure_json?: unknown;
};

export type AdvisorCatch = {
  id: string | number;
  session_id: string | number;
  caught_at: string;
  peg?: string | null;
  spot?: string | null;
  depth_ft?: number | string | null;
  distance_yds?: number | string | null;
  rig?: string | null;
  main_bait?: string | null;
  hookbait?: string | null;
  additive?: string | null;
  bait_amount?: number | string | null;
  bait_unit?: string | null;
  bait_notes?: string | null;
  source: "personal" | "community";
  /** True for recovered historical catches where the exact time is uncertain.
   *  Must be excluded from bite-time, catch-time weather, and temporal correlations. */
  caught_at_estimated?: boolean;
  /** IANA timezone captured for the catch location, e.g. Europe/Paris. */
  environment_timezone?: string | null;
  /** Whether this catch is from a cross-water (different lake) session. */
  is_cross_water?: boolean;
};

export type ConfidenceLevel =
  | "No evidence"
  | "Low Confidence"
  | "Medium Confidence"
  | "High Confidence";

export type AdvisorResult = {
  /** Prediction score 0–100, based on similarity-weighted success rate. NOT the raw catch rate. */
  fishingScore: number | null;
  /** null when there are zero comparable sessions — do not display "0/100". */
  hasScore: boolean;
  confidenceLevel: ConfidenceLevel;
  /** Actual historical catch rate as a percentage (0–100) among comparable sessions. */
  historicalCatchRatePct: number | null;
  recommendedArea: string | null;
  /** Qualifier label shown next to the area recommendation, e.g. "Limited evidence". */
  areaQualifier: string | null;
  recommendedDepthFt: number | null;
  depthQualifier: string | null;
  recommendedDistanceYds: number | null;
  distanceQualifier: string | null;
  bestBitePeriod: string | null;
  bitePeriodQualifier: string | null;
  recommendedRig: string | null;
  rigQualifier: string | null;
  recommendedMainBait: string | null;
  mainBaitQualifier: string | null;
  recommendedHookbait: string | null;
  hookbaitQualifier: string | null;
  recommendedAdditive: string | null;
  additiveQualifier: string | null;
  recommendedBaitAmount: number | null;
  recommendedBaitUnit: string | null;
  recommendedBaitApproach: string | null;
  baitApproachQualifier: string | null;
  recommendedTactic: string | null;
  tacticQualifier: string | null;
  alternativeTactic: string | null;
  explanations: Record<string, string>;
  evidenceSessionCount: number;
  effectiveEvidenceSessionCount: number;
  evidenceBlankSessionCount: number;
  evidenceCatchCount: number;
  personalSessionCount: number;
  communitySessionCount: number;
  communityIncluded: boolean;
  /** True when the result was built from cross-water fallback evidence (different lake). */
  isCrossWater: boolean;
};

// ─── Internal helpers ─────────────────────────────────────────────────────────

type ScoredSession = {
  session: AdvisorSession;
  similarity: number;
  completeness: number;
  seasonalMatch: number;
  recency: number;
};
type ScoredCatch = { item: AdvisorCatch; similarity: number };

const numeric = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clean = (value: unknown): string | null => {
  const result = String(value ?? "").trim();
  return result || null;
};

const LEGACY_ADDITIVE_TERMS =
  /\b(?:goo|liquid|attractor|smoke|booster|glug|dip|additive)\b/i;

function legacyAdditiveFromCatch(item: AdvisorCatch): string | null {
  const structured = clean(item.additive);
  if (structured) return structured;
  const legacyNote = clean(item.bait_notes);
  return legacyNote && LEGACY_ADDITIVE_TERMS.test(legacyNote)
    ? legacyNote
    : null;
}

function baitApproachNote(item: AdvisorCatch): string | null {
  const note = clean(item.bait_notes);
  if (!note) return null;
  return LEGACY_ADDITIVE_TERMS.test(note) ? null : note;
}

const normalized = (value: unknown): string | null =>
  clean(value)?.toLowerCase() ?? null;

function stringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => clean(item)).filter((item): item is string => Boolean(item));
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => clean(item))
            .filter((item): item is string => Boolean(item));
        }
      } catch {
        // Legacy plain text falls through below.
      }
    }
    return [trimmed];
  }
  return [];
}

function daysBetween(startAt: string, referenceDate: string): number | null {
  const start = new Date(startAt).getTime();
  const reference = new Date(referenceDate + "T12:00:00Z").getTime();
  if (!Number.isFinite(start) || !Number.isFinite(reference)) return null;
  return Math.max(0, (reference - start) / 86400000);
}

function safeTimeZone(value: string | null | undefined): string {
  const candidate = clean(value) ?? "Europe/London";
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: candidate }).format(new Date());
    return candidate;
  } catch {
    return "Europe/London";
  }
}

function localHour(value: string, timeZone?: string | null): number | null {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: safeTimeZone(timeZone),
    hour: "2-digit",
    hour12: false,
  })
    .formatToParts(new Date(value))
    .find((p) => p.type === "hour");
  return parts ? Number(parts.value) : null;
}

function localMonth(value: string, timeZone?: string | null): number | null {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: safeTimeZone(timeZone),
    month: "2-digit",
  })
    .formatToParts(new Date(value))
    .find((p) => p.type === "month");
  return parts ? Number(parts.value) : null;
}

function targetMonth(date: string): number | null {
  const m = Number(date.slice(5, 7));
  return m >= 1 && m <= 12 ? m : null;
}

function targetHour(time: string): number | null {
  const h = Number(time.slice(0, 2));
  return h >= 0 && h <= 23 ? h : null;
}

function hourDistance(a: number, b: number): number {
  const d = Math.abs(a - b);
  return Math.min(d, 24 - d);
}

function monthDistance(a: number, b: number): number {
  const d = Math.abs(a - b);
  return Math.min(d, 12 - d);
}

function seasonalRelevance(
  plannedMonth: number,
  historicalMonth: number,
  currentAirTempC: number | null,
  historicalAirTempC: unknown,
): number {
  const distance = monthDistance(plannedMonth, historicalMonth);
  const rolling = [1, 0.82, 0.52, 0.28, 0.12, 0.06, 0.04][distance] ?? 0.04;
  const historicalTemp = numeric(historicalAirTempC);
  if (currentAirTempC === null || historicalTemp === null) return rolling;

  // Weather can make a shoulder-season day behave more like a neighbouring month,
  // but it must not completely erase biological seasonality.
  const tempMatch = Math.max(0, 1 - Math.abs(historicalTemp - currentAirTempC) / 7);
  const weatherBridge = 0.35 * rolling + 0.65 * tempMatch;
  return Math.min(1, Math.max(rolling, weatherBridge * 0.82));
}

function windDirectionDegrees(value: string | null | undefined): number | null {
  const key = clean(value)?.toUpperCase().replace(/[^A-Z]/g, "") ?? "";
  const compass: Record<string, number> = {
    N: 0,
    NNE: 22.5,
    NE: 45,
    ENE: 67.5,
    E: 90,
    ESE: 112.5,
    SE: 135,
    SSE: 157.5,
    S: 180,
    SSW: 202.5,
    SW: 225,
    WSW: 247.5,
    W: 270,
    WNW: 292.5,
    NW: 315,
    NNW: 337.5,
  };
  return compass[key] ?? null;
}

function angularDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return Math.min(d, 360 - d);
}

function inTimeWindow(
  hour: number,
  start: number,
  durationHours: number,
): boolean {
  if (durationHours >= 24) return true;
  const end = (start + durationHours) % 24;
  return start <= end
    ? hour >= start && hour <= end
    : hour >= start || hour <= end;
}

function timePeriod(hour: number): string {
  if (hour < 6) return "Overnight";
  if (hour < 10) return "Early morning";
  if (hour < 14) return "Late morning";
  if (hour < 18) return "Afternoon";
  return "Evening";
}

function weatherKind(value: unknown): string | null {
  const t = normalized(value);
  if (!t) return null;
  if (/rain|drizzle|shower|wet|storm/.test(t)) return "rain";
  if (/overcast|cloud/.test(t)) return "cloud";
  if (/clear|sun|bright/.test(t)) return "clear";
  return "other";
}

function similarNumber(
  actual: unknown,
  expected: number | null,
  tolerance: number,
): number | null {
  const v = numeric(actual);
  if (v === null || expected === null) return null;
  return Math.max(0, 1 - Math.abs(v - expected) / tolerance);
}

/**
 * Returns condition similarity plus a completeness ratio. Missing historical
 * fields do not act as mismatches, but completeness is later used as a gentle
 * authority modifier so sparse records cannot look as strong as rich ones.
 */
function sessionSimilarity(
  session: AdvisorSession,
  input: AdvisorInput,
): { similarity: number; completeness: number; seasonalMatch: number } {
  const month = targetMonth(input.fishingDate);
  const startHour = targetHour(input.expectedStartTime);
  let points = 0;
  let available = 0;
  let possible = 0;

  // Rolling seasonal window. Neighbouring months retain strong relevance and
  // a close temperature match can partially bridge a shoulder-season mismatch.
  const hMonth = localMonth(session.start_at, session.environment_timezone);
  let seasonalMatch = 0;
  if (month !== null) {
    possible += 0.22;
    if (hMonth !== null) {
      available += 0.22;
      seasonalMatch = seasonalRelevance(
        month,
        hMonth,
        input.forecast.airTempC,
        session.air_temp_c,
      );
      points += seasonalMatch * 0.22;
    }
  }

  // Time of day (18%)
  const hHour = localHour(session.start_at, session.environment_timezone);
  if (startHour !== null) {
    possible += 0.18;
    if (hHour !== null) {
      available += 0.18;
      points += Math.max(0, 0.18 * (1 - hourDistance(startHour, hHour) / 8));
    }
  }

  // Numeric conditions. A target value creates potential evidence; missing
  // historical data lowers completeness rather than pretending a perfect match.
  const numericChecks: Array<[unknown, number | null, number, number]> = [
    [session.air_temp_c, input.forecast.airTempC, 6, 0.12],
    [session.air_pressure_hpa, input.forecast.pressureHpa, 18, 0.12],
    [session.wind_speed_mph, input.forecast.windSpeedMph, 12, 0.08],
    [session.cloud_cover_pct, input.forecast.cloudPct, 45, 0.08],
  ];
  for (const [actual, expected, tolerance, weight] of numericChecks) {
    if (expected === null) continue;
    possible += weight;
    const match = similarNumber(actual, expected, tolerance);
    if (match !== null) {
      available += weight;
      points += match * weight;
    }
  }

  // Wind direction (6%). Compass directions are compared angularly, so W and
  // SW are related evidence while opposing winds contribute little.
  const expectedWind = windDirectionDegrees(input.forecast.windDirection);
  if (expectedWind !== null) {
    possible += 0.06;
    const actualWind = windDirectionDegrees(session.wind_direction);
    if (actualWind !== null) {
      available += 0.06;
      points += Math.max(0, 1 - angularDistance(expectedWind, actualWind) / 90) * 0.06;
    }
  }

  // Pressure trend (6%)
  const expectedTrend = normalized(input.forecast.pressureTrend);
  if (expectedTrend) {
    possible += 0.06;
    const actualTrend = normalized(session.pressure_trend);
    if (actualTrend) {
      available += 0.06;
      if (expectedTrend === actualTrend) points += 0.06;
    }
  }

  // Rain (8%)
  const expectedRain =
    input.forecast.precipitationMm !== null
      ? input.forecast.precipitationMm > 0
      : null;
  if (expectedRain !== null) {
    possible += 0.08;
    const actualRain =
      session.rain_observed ??
      (weatherKind(session.weather) === "rain" ? true : null);
    if (actualRain !== null) {
      available += 0.08;
      if (expectedRain === actualRain) points += 0.08;
    }
  }

  const similarity = available ? points / available : 0;
  const completeness = possible ? Math.min(1, available / possible) : 1;
  return { similarity, completeness, seasonalMatch };
}

// ─── Recommendation helpers ───────────────────────────────────────────────────

type WeightedEntry = { value: string | null; weight: number };

/**
 * Most-weighted value, with optional session-level success-rate context.
 * Returns both the chosen value and the raw frequency/success breakdown.
 */
function weightedBest(rows: WeightedEntry[]): {
  value: string | null;
  topWeight: number;
  entryCount: number;
} {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const v = clean(row.value);
    if (v) totals.set(v, (totals.get(v) ?? 0) + row.weight);
  }
  const sorted = [...totals.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  return {
    value: sorted[0]?.[0] ?? null,
    topWeight: sorted[0]?.[1] ?? 0,
    entryCount: totals.size,
  };
}

/**
 * For a given attribute (rig, bait, etc.), count how many distinct comparable
 * sessions that attribute appeared in, and how many of those produced a catch.
 * Used to report success-rate rather than simple catch totals.
 */
function attributeSessionStats(
  attribute: string,
  scoredCatches: ScoredCatch[],
  sessionWeights: Map<string, number>,
  allSessions: ScoredSession[],
): { sessionCount: number; successfulSessionCount: number } {
  // Find unique sessions that had a catch with this attribute
  const sessionsWithAttribute = new Set(
    scoredCatches
      .filter((c) => normalized(c.item.rig ?? c.item.main_bait ?? c.item.hookbait) === normalized(attribute) || clean(c.item.rig) === attribute || clean(c.item.main_bait) === attribute || clean(c.item.hookbait) === attribute)
      .map((c) => String(c.item.session_id)),
  );
  const successfulCount = [...sessionsWithAttribute].filter((sid) =>
    allSessions.some(
      (s) =>
        String(s.session.session_id ?? s.session.id) === sid &&
        Number(s.session.catch_count) > 0,
    ),
  ).length;
  return {
    sessionCount: sessionsWithAttribute.size,
    successfulSessionCount: successfulCount,
  };
}

function weightedMedian(
  rows: Array<{ value: number | null; weight: number }>,
): number | null {
  const usable = rows
    .filter((r) => r.value !== null)
    .sort((a, b) => (a.value as number) - (b.value as number));
  if (!usable.length) return null;
  const total = usable.reduce((s, r) => s + r.weight, 0);
  let running = 0;
  for (const row of usable) {
    running += row.weight;
    if (running >= total / 2) return row.value;
  }
  return usable[usable.length - 1].value;
}

function tacticFromCatch(item: AdvisorCatch): string | null {
  const text = `${item.hookbait ?? ""} ${item.rig ?? ""}`.toLowerCase();
  if (/zig/.test(text)) return "Zig";
  if (/pop[- ]?up|popup/.test(text)) return "Pop-up";
  if (/wafter/.test(text)) return "Wafter";
  if (/bottom bait|bottom-bait|boilie/.test(text)) return "Bottom bait";
  return null;
}

function evidenceQualifier(
  catchCount: number,
  sessionCount: number,
): string | null {
  if (catchCount === 0) return null;
  if (catchCount === 1) return "Single observation";
  if (catchCount < 4) return "Limited evidence";
  if (sessionCount < 5) return "Small sample";
  return null; // enough evidence — no qualifier needed
}

function sourcePhrase(personal: number, community: number): string {
  if (personal && community)
    return `your ${personal} angler-session${personal === 1 ? "" : "s"} and ${community} anonymous community session${community === 1 ? "" : "s"}`;
  if (personal)
    return `your ${personal} angler-session${personal === 1 ? "" : "s"}`;
  if (community)
    return `${community} anonymous community session${community === 1 ? "" : "s"}`;
  return "comparable angler-sessions";
}

function sourceSupportForValue(
  rows: ScoredCatch[],
  selectedValue: string | null,
  valueOf: (item: AdvisorCatch) => string | null,
): { personal: number; community: number; total: number } {
  if (!selectedValue) return { personal: 0, community: 0, total: 0 };
  const personal = new Set<string>();
  const community = new Set<string>();
  for (const row of rows) {
    if (normalized(valueOf(row.item)) !== normalized(selectedValue)) continue;
    const id = String(row.item.session_id);
    if (row.item.source === "personal") personal.add(id);
    else community.add(id);
  }
  return {
    personal: personal.size,
    community: community.size,
    total: personal.size + community.size,
  };
}

type SetupEvidenceKey =
  | "setup_rigs"
  | "setup_main_baits"
  | "setup_hookbaits"
  | "setup_additives"
  | "setup_bait_approaches";

type PatternStatus =
  | "Emerging"
  | "Supported"
  | "Established"
  | "Changing"
  | "Contradicted";

type PatternEvidenceSummary = {
  status: PatternStatus;
  supportingSessions: number;
  supportingCatches: number;
  personalSupportingSessions: number;
  communitySupportingSessions: number;
  blankContradictions: number;
  recentSupportingSessions: number;
  recentBlankContradictions: number;
  seasonalSupportingSessions: number;
  closeConditionSupportingSessions: number;
};

function setupValues(
  session: AdvisorSession,
  key: SetupEvidenceKey | null,
): string[] {
  return key ? stringList(session[key]) : [];
}

function sessionPhysicalId(session: AdvisorSession): string {
  return String(session.session_id ?? session.id);
}

function setupExposureHours(
  session: AdvisorSession,
  key: SetupEvidenceKey,
  selectedValue: string,
): number | null {
  const selected = normalized(selectedValue);
  if (!selected) return null;
  let rows: any[] = [];
  const raw = session.setup_exposure_json;
  if (Array.isArray(raw)) rows = raw;
  else if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) rows = parsed;
    } catch {
      return null;
    }
  }
  if (!rows.length) return null;

  const field =
    key === "setup_rigs"
      ? "rig"
      : key === "setup_main_baits"
        ? "main_bait"
        : key === "setup_hookbaits"
          ? "hookbait"
          : key === "setup_additives"
            ? "additive"
            : "bait_approach";

  let hours = 0;
  let matched = false;
  for (const row of rows) {
    if (normalized(row?.[field]) !== selected) continue;
    const value = numeric(row?.hours);
    if (value === null || value < 0) continue;
    matched = true;
    hours += value;
  }
  return matched ? hours : null;
}

function weightedBestCatchValue(
  rows: ScoredCatch[],
  valueOf: (item: AdvisorCatch) => string | null,
  exposure?: {
    sessions: ScoredSession[];
    setupField: SetupEvidenceKey;
  },
): {
  value: string | null;
  score: number;
  sessionCount: number;
  catchCount: number;
  blankContradictionCount: number;
} {
  const candidates = new Map<
    string,
    {
      display: string;
      score: number;
      sessions: Map<string, number>;
      catchCount: number;
      blankPenalty: number;
      blankContradictionCount: number;
    }
  >();

  for (const row of rows) {
    const display = clean(valueOf(row.item));
    if (!display) continue;
    const key = normalized(display);
    if (!key) continue;
    const candidate = candidates.get(key) ?? {
      display,
      score: 0,
      sessions: new Map<string, number>(),
      catchCount: 0,
      blankPenalty: 0,
      blankContradictionCount: 0,
    };
    candidate.catchCount += 1;
    const sessionId = String(row.item.session_id);
    const previous = candidate.sessions.get(sessionId) ?? 0;
    if (row.similarity > previous) {
      candidate.sessions.set(sessionId, row.similarity);
      candidate.score += row.similarity - previous;
    }
    candidates.set(key, candidate);
  }

  // A blank is only contradiction evidence when CatchCompass knows the selected
  // setup element was actually fished. Timed setup exposure scales the penalty:
  // a tactic tried briefly is weaker negative evidence than one fished for most
  // of the session. Older catch-only sessions stay neutral.
  if (exposure) {
    for (const candidate of candidates.values()) {
      const candidateKey = normalized(candidate.display);
      if (!candidateKey) continue;
      for (const row of exposure.sessions) {
        if (row.session.denominator_eligible === false) continue;
        if (Number(row.session.catch_count) > 0) continue;
        const used = setupValues(row.session, exposure.setupField).some(
          (value) => normalized(value) === candidateKey,
        );
        if (!used) continue;
        candidate.blankContradictionCount += 1;
        const exposureHours = setupExposureHours(
          row.session,
          exposure.setupField,
          candidate.display,
        );
        const sessionHours = numeric(row.session.duration_hours);
        const exposureShare =
          exposureHours === null || sessionHours === null || sessionHours <= 0
            ? 1
            : Math.max(0.2, Math.min(1, exposureHours / sessionHours));
        candidate.blankPenalty += row.similarity * 0.45 * exposureShare;
      }
    }
  }

  const ranked = [...candidates.values()].sort(
    (a, b) =>
      b.score - b.blankPenalty - (a.score - a.blankPenalty) ||
      b.sessions.size - a.sessions.size ||
      b.catchCount - a.catchCount ||
      a.display.localeCompare(b.display),
  );
  const best = ranked[0];
  return best
    ? {
        value: best.display,
        score: Math.max(0, best.score - best.blankPenalty),
        sessionCount: best.sessions.size,
        catchCount: best.catchCount,
        blankContradictionCount: best.blankContradictionCount,
      }
    : {
        value: null,
        score: 0,
        sessionCount: 0,
        catchCount: 0,
        blankContradictionCount: 0,
      };
}

function patternEvidenceForValue(
  selectedValue: string | null,
  rows: ScoredCatch[],
  sessions: ScoredSession[],
  input: AdvisorInput,
  valueOf: (item: AdvisorCatch) => string | null,
  setupField: SetupEvidenceKey | null = null,
  sessionExposureValues?: (session: AdvisorSession) => string[],
): PatternEvidenceSummary | null {
  if (!selectedValue) return null;
  const selectedKey = normalized(selectedValue);
  if (!selectedKey) return null;

  const supportRows = rows.filter(
    (row) => normalized(valueOf(row.item)) === selectedKey,
  );
  if (!supportRows.length) return null;

  const supportSessionIds = new Set(
    supportRows.map((row) => String(row.item.session_id)),
  );
  const sessionMap = new Map(
    sessions.map((row) => [sessionPhysicalId(row.session), row]),
  );
  const supportSessions = [...supportSessionIds]
    .map((id) => sessionMap.get(id))
    .filter((row): row is ScoredSession => Boolean(row));

  const personalIds = new Set<string>();
  const communityIds = new Set<string>();
  for (const row of supportRows) {
    const sid = String(row.item.session_id);
    if (row.item.source === "personal") personalIds.add(sid);
    else communityIds.add(sid);
  }

  const recentSupportingSessions = supportSessions.filter((row) => {
    const age = daysBetween(row.session.start_at, input.fishingDate);
    return age !== null && age <= 180;
  }).length;
  const seasonalSupportingSessions = supportSessions.filter(
    (row) => row.seasonalMatch >= 0.72,
  ).length;
  const closeConditionSupportingSessions = supportSessions.filter(
    (row) => row.similarity >= 0.5 && row.completeness >= 0.45,
  ).length;

  const blankRows =
    setupField || sessionExposureValues
      ? sessions.filter((row) => {
          if (row.session.denominator_eligible === false) return false;
          if (Number(row.session.catch_count) > 0) return false;
          const values = setupField
            ? setupValues(row.session, setupField)
            : sessionExposureValues?.(row.session) ?? [];
          return values.some((value) => normalized(value) === selectedKey);
        })
      : [];
  const recentBlankContradictions = blankRows.filter((row) => {
    const age = daysBetween(row.session.start_at, input.fishingDate);
    return age !== null && age <= 180;
  }).length;

  const supportingSessions = supportSessionIds.size;
  const historicalSupport = Math.max(
    0,
    supportingSessions - recentSupportingSessions,
  );
  let status: PatternStatus = "Emerging";
  if (
    historicalSupport >= 3 &&
    recentBlankContradictions >= 2 &&
    recentSupportingSessions === 0
  ) {
    status = "Changing";
  } else if (
    blankRows.length >= 3 &&
    blankRows.length > supportingSessions
  ) {
    status = "Contradicted";
  } else if (
    supportingSessions >= 6 &&
    blankRows.length <= Math.max(1, Math.floor(supportingSessions * 0.25))
  ) {
    status = "Established";
  } else if (supportingSessions >= 3) {
    status = "Supported";
  }

  const crossWaterOnly =
    supportRows.length > 0 &&
    supportRows.every((row) => row.item.is_cross_water === true);
  if (crossWaterOnly && status === "Established") {
    status = "Supported";
  }

  return {
    status,
    supportingSessions,
    supportingCatches: supportRows.length,
    personalSupportingSessions: personalIds.size,
    communitySupportingSessions: communityIds.size,
    blankContradictions: blankRows.length,
    recentSupportingSessions,
    recentBlankContradictions,
    seasonalSupportingSessions,
    closeConditionSupportingSessions,
  };
}

function patternStrengthExplanation(
  label: string,
  evidence: PatternEvidenceSummary | null,
): string | null {
  if (!evidence) return null;
  const source = sourcePhrase(
    evidence.personalSupportingSessions,
    evidence.communitySupportingSessions,
  );
  const contradiction = evidence.blankContradictions
    ? ` ${evidence.blankContradictions} comparable blank session${evidence.blankContradictions === 1 ? "" : "s"} are confirmed to have used this same ${label}, so they count as genuine contradiction evidence.`
    : " No comparable blank session is currently confirmed to have used this same setup element.";
  return `Supported by ${evidence.supportingSessions} independent comparable session${evidence.supportingSessions === 1 ? "" : "s"} and ${evidence.supportingCatches} catch${evidence.supportingCatches === 1 ? "" : "es"} from ${source}.${contradiction}`;
}

function whyThisFitsToday(
  evidence: PatternEvidenceSummary | null,
): string | null {
  if (!evidence) return null;
  const total = evidence.supportingSessions;
  const parts: string[] = [];

  parts.push(
    `${evidence.seasonalSupportingSessions} of ${total} supporting session${total === 1 ? "" : "s"} ${evidence.seasonalSupportingSessions === 1 ? "sits" : "sit"} in the current rolling seasonal window`,
  );

  if (evidence.closeConditionSupportingSessions > 0) {
    parts.push(
      `${evidence.closeConditionSupportingSessions} of ${total} ${evidence.closeConditionSupportingSessions === 1 ? "is" : "are"} a close overall conditions match`,
    );
  } else {
    parts.push("none of the supporting sessions is a particularly close recorded-conditions match");
  }

  if (evidence.recentSupportingSessions > 0) {
    parts.push(
      `${evidence.recentSupportingSessions} of ${total} supporting session${total === 1 ? "" : "s"} ${evidence.recentSupportingSessions === 1 ? "is" : "are"} from the last 180 days`,
    );
  } else {
    parts.push("the supporting evidence is older than 180 days");
  }

  const recentWarning =
    evidence.recentBlankContradictions > 0
      ? ` Recent contradiction: ${evidence.recentBlankContradictions} blank session${evidence.recentBlankContradictions === 1 ? " has" : "s have"} also used this setup element.`
      : "";
  return `${parts.join("; ")}.${recentWarning}`;
}

// ─── Duration exposure weight ────────────────────────────────────────────────

/**
 * Duration is exposure evidence, not a punishment for a successful session.
 * Successful sessions of at least one hour retain full duration weight.
 * Sub-hour sessions remain deliberately sceptical, whether successful or blank.
 * Blanks are duration-weighted because a short blank is weaker negative evidence
 * than a long blank. Unknown duration is neutral.
 */
function durationWeight(
  hours: number | string | null | undefined,
  wasSuccessful = false,
): number {
  const h = numeric(hours);
  if (h === null || h <= 0) return 1;
  if (h < 1) return 0.5;
  if (wasSuccessful) return 1;
  const capped = Math.min(h, 24);
  return Math.max(0.3, Math.sqrt(capped / 24));
}

// ─── Recency decay ────────────────────────────────────────────────────────────

/**
 * Returns a recency weight [0.25, 1.0] for a session.
 * Recent evidence carries more influence; older still contributes.
 * Half-life ≈ 12 months. Never reaches zero for valid older evidence.
 */
function recencyWeight(startAt: string, referenceDate: string): number {
  try {
    const sessionMs = new Date(startAt).getTime();
    const refMs = new Date(referenceDate + "T12:00:00Z").getTime();
    const ageMs = Math.max(0, refMs - sessionMs);
    const ageDays = ageMs / 86400000;
    // Exponential decay: half-life 365 days, floor at 0.25
    return Math.max(0.25, Math.exp(-0.693 * ageDays / 365));
  } catch {
    return 0.5;
  }
}

// ─── Confidence thresholds (requirements: 0=No evidence, 1–24=Low, 25–99=Medium, 100+=High) ──

function confidenceFromCount(count: number): ConfidenceLevel {
  if (count === 0) return "No evidence";
  if (count < 25) return "Low Confidence";
  if (count < 100) return "Medium Confidence";
  return "High Confidence";
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function buildAdvisorRecommendation(
  input: AdvisorInput,
  sessions: AdvisorSession[],
  catches: AdvisorCatch[],
  options?: {
    isCrossWater?: boolean;
    /**
     * Transferable-tactic fallback keeps otherwise weak cross-water successful
     * sessions available for rig/bait/tactic fields. It can combine the angler's
     * own history with anonymous community history, but it never turns those
     * records into lake-specific evidence.
     */
    transferableTacticFallback?: boolean;
    /**
     * Future Trip same-lake mode has no detailed weather forecast, so constrain
     * native evidence to a rolling ±1 month window rather than one exact month.
     */
    requireNearbyMonth?: boolean;
  },
): AdvisorResult {
  const isCrossWater = options?.isCrossWater ?? false;
  const transferableTacticFallback = options?.transferableTacticFallback ?? false;
  const requireNearbyMonth = options?.requireNearbyMonth ?? false;

  // ── Filter comparable sessions (combined similarity ≥ 0.2) ──
  // Apply recency decay and duration weighting to each session's base similarity.
  // Personal evidence gets a modest 1.15× multiplier over community for the same similarity.
  // Personalisation is an advantage, not a trump card: a stronger anonymous
  // community sample can still win when its evidence is materially better.
  const plannedMonth = targetMonth(input.fishingDate);
  const scoredSessions: ScoredSession[] = sessions
    .map((session) => {
      if (requireNearbyMonth && plannedMonth !== null) {
        const historicalMonth = localMonth(
          session.start_at,
          session.environment_timezone,
        );
        if (
          historicalMonth === null ||
          monthDistance(historicalMonth, plannedMonth) > 1
        ) {
          return {
            session,
            similarity: 0,
            completeness: 0,
            seasonalMatch: 0,
            recency: 0,
          };
        }
      }

      const observed = sessionSimilarity(session, input);
      // A successful tactic from another water can still be useful personal
      // evidence even when season/weather are not a close match. In fallback
      // mode retain a small baseline weight, while still allowing genuinely
      // comparable sessions to outrank weaker matches.
      const baseSim = transferableTacticFallback
        ? Math.max(0.6, observed.similarity)
        : observed.similarity;
      const rec = recencyWeight(session.start_at, input.fishingDate);
      const wasSuccessful = Number(session.catch_count) > 0;
      const dur = durationWeight(session.duration_hours, wasSuccessful);
      const sourceBonus = session.source === "personal" ? 1.15 : 1.0;
      // Missing historical fields do not invalidate a session, but a record
      // matched on only a few known factors should not carry the same authority
      // as a complete record. Keep the modifier gentle (0.70–1.00).
      const completenessWeight = 0.7 + 0.3 * observed.completeness;
      const combined =
        Math.min(1, baseSim * rec * sourceBonus * completenessWeight) * dur;
      return {
        session,
        similarity: combined,
        completeness: observed.completeness,
        seasonalMatch: observed.seasonalMatch,
        recency: rec,
      };
    })
    .filter((row) => row.similarity >= 0.2);

  // Catches use the physical session id. Personal session rows use the
  // session_angler id as `id`, so key by session_id when it is available.
  const sessionWeights = new Map(
    scoredSessions.map((row) => [
      String(row.session.session_id ?? row.session.id),
      row.similarity,
    ]),
  );

  // ── Map comparable catches ──
  const scoredCatches: ScoredCatch[] = catches
    .map((item) => ({
      item,
      similarity: sessionWeights.get(String(item.session_id)) ?? 0,
    }))
    .filter((row) => row.similarity > 0);

  // ── Temporal-eligible catches: exclude caught_at_estimated ──
  // Used for bite-time and catch-time weather/temporal correlations only.
  const temporalCatches = scoredCatches.filter(
    (row) => !row.item.caught_at_estimated,
  );

  // ── Evidence counts ──
  // Species-target exposure is the denominator. A session targeting carp that
  // happens to catch catfish is a carp blank, but its catfish catches remain
  // useful positive catfish observations for bait/rig/area/time patterns.
  const denominatorSessions = scoredSessions.filter(
    (row) => row.session.denominator_eligible !== false,
  );
  const incidentalSessions = scoredSessions.filter(
    (row) => row.session.denominator_eligible === false,
  );
  const incidentalSessionIds = new Set(
    incidentalSessions.map((row) =>
      String(row.session.session_id ?? row.session.id),
    ),
  );
  const incidentalCatchCount = scoredCatches.filter((row) =>
    incidentalSessionIds.has(String(row.item.session_id)),
  ).length;
  const incidentalPositiveSessions = incidentalSessions.filter(
    (row) => Number(row.session.catch_count) > 0,
  );
  // An incidental catch is real positive evidence that the species was present,
  // feeding and catchable in those conditions, but it is weaker than a session
  // deliberately targeting that species. It can lift the prediction score, while
  // a non-catch during another-species session stays neutral and never becomes a blank.
  const INCIDENTAL_POSITIVE_FACTOR = 0.5;
  const incidentalPositiveWeight = incidentalPositiveSessions.reduce(
    (sum, row) => sum + row.similarity * INCIDENTAL_POSITIVE_FACTOR,
    0,
  );

  const evidenceSessionCount = denominatorSessions.length;
  const effectiveEvidenceSessionCount = Number(
    denominatorSessions.reduce((sum, row) => sum + row.similarity, 0).toFixed(1),
  );
  const evidenceBlankSessionCount = denominatorSessions.filter(
    (row) => Number(row.session.catch_count) === 0,
  ).length;
  const evidenceCatchCount = scoredCatches.length;
  const personalSessionCount = denominatorSessions.filter(
    (row) => row.session.source === "personal",
  ).length;
  const communitySessionCount = denominatorSessions.filter(
    (row) => row.session.source === "community",
  ).length;
  const communityIncluded =
    communitySessionCount > 0 ||
    scoredCatches.some((row) => row.item.source === "community");
  const evidencePhrase = sourcePhrase(personalSessionCount, communitySessionCount);
  const estimatedCatchCount = scoredCatches.filter(
    (row) => row.item.caught_at_estimated,
  ).length;

  // ── Confidence ──
  // Cross-water fallback is always Low Confidence. Same-lake incidental catches
  // contribute reduced positive evidence, so they can create a low-confidence
  // score even before a deliberately targeted session exists.
  const scoreEvidenceStrength =
    effectiveEvidenceSessionCount + incidentalPositiveWeight;
  const rawConfidence = confidenceFromCount(effectiveEvidenceSessionCount);
  const confidenceLevel: ConfidenceLevel = isCrossWater
    ? "Low Confidence"
    : evidenceSessionCount === 0 && incidentalPositiveWeight > 0
      ? "Low Confidence"
      : rawConfidence;

  // ── Fishing Score: targeted exposure + reduced incidental positives ──
  // Targeted sessions contribute both success and blank evidence. An incidental
  // catch contributes positive-only evidence at reduced strength. A session
  // targeting another species with no catch of this species contributes nothing.
  const targetedTotalWeight = denominatorSessions.reduce(
    (s, r) => s + r.similarity,
    0,
  );
  const targetedSuccessWeight = denominatorSessions.reduce(
    (s, r) => s + (Number(r.session.catch_count) > 0 ? r.similarity : 0),
    0,
  );
  const totalWeight = targetedTotalWeight + incidentalPositiveWeight;
  const successWeight = targetedSuccessWeight + incidentalPositiveWeight;
  const hasScore = totalWeight > 0;
  let rawFishingScore = hasScore
    ? Math.round((successWeight / totalWeight) * 100)
    : null;
  // Cross-water: bound the score conservatively to 45–55.
  if (isCrossWater && rawFishingScore !== null) {
    rawFishingScore = Math.min(55, Math.max(45, rawFishingScore));
  }

  // ── Sample-size shrinkage toward neutral 50 ──
  // A tiny sample must not produce extreme scores (e.g. 1 successful session → 100).
  // Formula: adjusted = round(50 + shrinkageFactor × (raw − 50))
  // where shrinkageFactor = n / (n + K), K = 10.
  // Effect:
  //   n=1  → factor ≈ 0.09 (strong pull to 50)
  //   n=5  → factor ≈ 0.33
  //   n=25 → factor ≈ 0.71
  //   n=100→ factor ≈ 0.91 (close to raw)
  // Applied AFTER the cross-water clamp so the 45–55 bound is never widened.
  // historicalCatchRatePct is computed separately and is never touched here.
  const SHRINKAGE_K = 10;
  const fishingScore =
    rawFishingScore !== null
      ? (() => {
          const sf =
            scoreEvidenceStrength /
            (scoreEvidenceStrength + SHRINKAGE_K);
          return Math.round(50 + sf * (rawFishingScore - 50));
        })()
      : null;

  // ── Historical catch rate: simple count (what actually happened) ──
  const historicalCatchRatePct =
    evidenceSessionCount > 0
      ? Math.round(
          ((evidenceSessionCount - evidenceBlankSessionCount) /
            evidenceSessionCount) *
            100,
        )
      : null;

  // ── Bite period — temporal-eligible catches only (exclude caught_at_estimated) ──
  const scoredInWindow = temporalCatches.filter((row) => {
    const hour = localHour(row.item.caught_at, row.item.environment_timezone);
    const start = targetHour(input.expectedStartTime);
    return (
      hour !== null &&
      start !== null &&
      inTimeWindow(hour, start, input.expectedDurationHours)
    );
  });
  const bitePeriodBest = weightedBest(
    scoredInWindow.map((row) => {
      const h = localHour(row.item.caught_at, row.item.environment_timezone);
      return { value: h !== null ? timePeriod(h) : null, weight: row.similarity };
    }),
  );
  const bestBitePeriod = isCrossWater ? null : bitePeriodBest.value; // cross-water: no lake-specific bite window
  const bitePeriodQualifier = evidenceQualifier(scoredInWindow.length, personalSessionCount + communitySessionCount);

  // ── Peg / swim ──
  // The headline "Where" recommendation must stay at peg/swim level. A saved
  // spot is useful context inside that peg, but is too precise and changeable
  // to become the main location recommendation.
  const areaRows: WeightedEntry[] = scoredCatches
    .filter((r) => clean(r.item.peg))
    .map((r) => ({ value: r.item.peg ?? null, weight: r.similarity }));
  const areaBest = weightedBest(areaRows);
  const recommendedArea = areaBest.value;
  const areaQualifier =
    areaBest.entryCount > 0
      ? evidenceQualifier(areaRows.length, scoredSessions.length) ??
        (areaRows.length < 3 ? "Possible area based on limited evidence" : null)
      : null;

  // ── Depth ──
  const depthCatches = scoredCatches.filter(
    (r) => numeric(r.item.depth_ft) !== null,
  );
  const recommendedDepthFt = weightedMedian(
    depthCatches.map((r) => ({ value: numeric(r.item.depth_ft), weight: r.similarity })),
  );
  const depthQualifier = evidenceQualifier(depthCatches.length, scoredSessions.length);

  // ── Distance ──
  const distanceCatches = scoredCatches.filter(
    (r) => numeric(r.item.distance_yds) !== null,
  );
  const recommendedDistanceYds = weightedMedian(
    distanceCatches.map((r) => ({
      value: numeric(r.item.distance_yds),
      weight: r.similarity,
    })),
  );
  const distanceQualifier = evidenceQualifier(distanceCatches.length, scoredSessions.length);

  // ── Rig ──
  const rigBest = weightedBestCatchValue(
    scoredCatches,
    (item) => item.rig ?? null,
    { sessions: scoredSessions, setupField: "setup_rigs" },
  );
  const recommendedRig = rigBest.value;
  const rigSessionStats = recommendedRig
    ? attributeSessionStats(recommendedRig, scoredCatches, sessionWeights, scoredSessions)
    : { sessionCount: 0, successfulSessionCount: 0 };
  const rigQualifier = recommendedRig
    ? evidenceQualifier(rigSessionStats.sessionCount, evidenceSessionCount) ??
      (rigSessionStats.sessionCount === 1 ? "Observed successful tactic" : null)
    : null;
  const rigSourceSupport = sourceSupportForValue(
    scoredCatches,
    recommendedRig,
    (item) => clean(item.rig),
  );

  // ── Main bait ──
  const mainBaitBest = weightedBestCatchValue(
    scoredCatches,
    (item) => item.main_bait ?? null,
    { sessions: scoredSessions, setupField: "setup_main_baits" },
  );
  const recommendedMainBait = mainBaitBest.value;
  const mainBaitQualifier = mainBaitBest.value
    ? evidenceQualifier(
        scoredCatches.filter((r) => clean(r.item.main_bait) === mainBaitBest.value).length,
        evidenceSessionCount,
      )
    : null;
  const mainBaitSourceSupport = sourceSupportForValue(
    scoredCatches,
    recommendedMainBait,
    (item) => clean(item.main_bait),
  );

  // ── Hookbait ──
  const hookbaitBest = weightedBestCatchValue(
    scoredCatches,
    (item) => item.hookbait ?? null,
    { sessions: scoredSessions, setupField: "setup_hookbaits" },
  );
  const recommendedHookbait = hookbaitBest.value;
  const hookbaitQualifier = hookbaitBest.value
    ? evidenceQualifier(
        scoredCatches.filter((r) => clean(r.item.hookbait) === hookbaitBest.value).length,
        evidenceSessionCount,
      )
    : null;
  const hookbaitSourceSupport = sourceSupportForValue(
    scoredCatches,
    recommendedHookbait,
    (item) => clean(item.hookbait),
  );

  // ── Additive / attractor ──
  const additiveBest = weightedBestCatchValue(
    scoredCatches,
    (item) => legacyAdditiveFromCatch(item),
    { sessions: scoredSessions, setupField: "setup_additives" },
  );
  const recommendedAdditive = additiveBest.value;
  const additiveQualifier = recommendedAdditive
    ? evidenceQualifier(additiveBest.sessionCount, evidenceSessionCount)
    : null;
  const additiveSourceSupport = sourceSupportForValue(
    scoredCatches,
    recommendedAdditive,
    (item) => legacyAdditiveFromCatch(item),
  );

  // ── Bait amount / approach ──
  // Choose the unit with the strongest independent-session support, then use a
  // weighted median amount. This prevents one unusually close catch from
  // dictating the entire baiting quantity recommendation.
  const baitUnitSupport = new Map<
    string,
    { display: string; score: number; sessions: Set<string> }
  >();
  for (const row of scoredCatches) {
    const unit = clean(row.item.bait_unit);
    const amount = numeric(row.item.bait_amount);
    if (!unit || amount === null) continue;
    const key = normalized(unit)!;
    const current = baitUnitSupport.get(key) ?? {
      display: unit,
      score: 0,
      sessions: new Set<string>(),
    };
    const sid = String(row.item.session_id);
    if (!current.sessions.has(sid)) {
      current.sessions.add(sid);
      current.score += row.similarity;
    }
    baitUnitSupport.set(key, current);
  }
  const baitUnitWinner =
    [...baitUnitSupport.entries()].sort(
      (a, b) =>
        b[1].score - a[1].score ||
        b[1].sessions.size - a[1].sessions.size ||
        a[0].localeCompare(b[0]),
    )[0] ?? null;
  const recommendedBaitUnit = baitUnitWinner?.[1].display ?? null;
  const recommendedBaitUnitKey = baitUnitWinner?.[0] ?? null;
  const baitAmountRows = recommendedBaitUnitKey
    ? scoredCatches.filter(
        (row) =>
          normalized(row.item.bait_unit) === recommendedBaitUnitKey &&
          numeric(row.item.bait_amount) !== null,
      )
    : [];
  const recommendedBaitAmount = weightedMedian(
    baitAmountRows.map((row) => ({
      value: numeric(row.item.bait_amount),
      weight: row.similarity,
    })),
  );
  // Exact quantities stay as evidence only. Feeding changes with season,
  // fish response, stock pressure and session context, so never promote a
  // weighted-median amount into a fixed recommendation. Feeding STYLE can be
  // recommended when independent-session evidence supports it.
  const baitApproachBest = weightedBestCatchValue(
    scoredCatches,
    (item) => baitApproachNote(item),
    { sessions: scoredSessions, setupField: "setup_bait_approaches" },
  );
  const recommendedBaitApproach = baitApproachBest.value;
  const baitApproachQualifier = recommendedBaitApproach
    ? evidenceQualifier(baitApproachBest.sessionCount, evidenceSessionCount)
    : null;

  // ── Tactic ──
  const tacticBest = weightedBestCatchValue(
    scoredCatches,
    (item) => tacticFromCatch(item),
  );
  const recommendedTactic = tacticBest.value;
  const tacticCatchCount = tacticBest.catchCount;
  const tacticQualifier = recommendedTactic
    ? evidenceQualifier(tacticCatchCount, evidenceSessionCount) ??
      (tacticCatchCount === 1 ? "Observed successful tactic" : null)
    : null;
  const tacticSourceSupport = sourceSupportForValue(
    scoredCatches,
    recommendedTactic,
    (item) => tacticFromCatch(item),
  );

  // ── Pattern evidence ledger ──
  // Independent sessions are the authority unit. Catch totals add context but
  // cannot inflate a pattern. Blank contradiction is only counted when a rod
  // setup confirms that the same element was actually fished.
  const areaPattern = patternEvidenceForValue(
    recommendedArea,
    scoredCatches,
    scoredSessions,
    input,
    (item) => clean(item.peg),
    null,
    (session) => (clean(session.peg) ? [clean(session.peg) as string] : []),
  );
  const bitePeriodPattern = patternEvidenceForValue(
    bestBitePeriod,
    temporalCatches,
    scoredSessions,
    input,
    (item) => {
      const hour = localHour(item.caught_at, item.environment_timezone);
      return hour === null ? null : timePeriod(hour);
    },
  );
  const rigPattern = patternEvidenceForValue(
    recommendedRig,
    scoredCatches,
    scoredSessions,
    input,
    (item) => clean(item.rig),
    "setup_rigs",
  );
  const mainBaitPattern = patternEvidenceForValue(
    recommendedMainBait,
    scoredCatches,
    scoredSessions,
    input,
    (item) => clean(item.main_bait),
    "setup_main_baits",
  );
  const hookbaitPattern = patternEvidenceForValue(
    recommendedHookbait,
    scoredCatches,
    scoredSessions,
    input,
    (item) => clean(item.hookbait),
    "setup_hookbaits",
  );
  const additivePattern = patternEvidenceForValue(
    recommendedAdditive,
    scoredCatches,
    scoredSessions,
    input,
    (item) => legacyAdditiveFromCatch(item),
    "setup_additives",
  );
  const baitApproachPattern = patternEvidenceForValue(
    recommendedBaitApproach,
    scoredCatches,
    scoredSessions,
    input,
    (item) => baitApproachNote(item),
    "setup_bait_approaches",
  );
  const tacticPattern = patternEvidenceForValue(
    recommendedTactic,
    scoredCatches,
    scoredSessions,
    input,
    (item) => tacticFromCatch(item),
  );

  // Rank distinct complete tactic combinations by independent supporting
  // sessions rather than simply taking the next single catch. Multiple fish
  // from one session reinforce the observation but do not pretend to be
  // independent sessions.
  const planCandidates = new Map<
    string,
    { item: AdvisorCatch; score: number; sessions: Set<string> }
  >();
  for (const row of scoredCatches) {
    const parts = [
      tacticFromCatch(row.item),
      clean(row.item.rig),
      clean(row.item.main_bait),
      clean(row.item.hookbait),
      legacyAdditiveFromCatch(row.item),
    ];
    if (!parts.some(Boolean)) continue;
    const key = parts.map((part) => normalized(part) ?? "").join("|");
    const existing = planCandidates.get(key) ?? {
      item: row.item,
      score: 0,
      sessions: new Set<string>(),
    };
    const sessionId = String(row.item.session_id);
    if (!existing.sessions.has(sessionId)) {
      existing.sessions.add(sessionId);
      existing.score += row.similarity;
    }
    planCandidates.set(key, existing);
  }

  const primaryPlanKey = [
    recommendedTactic,
    recommendedRig,
    recommendedMainBait,
    recommendedHookbait,
    recommendedAdditive,
  ]
    .map((part) => normalized(part) ?? "")
    .join("|");
  const alternativeCandidate = [...planCandidates.entries()]
    .filter(([key]) => key !== primaryPlanKey)
    .sort(
      (a, b) =>
        b[1].score - a[1].score ||
        b[1].sessions.size - a[1].sessions.size ||
        a[0].localeCompare(b[0]),
    )[0]?.[1] ?? null;

  const alternativeTactic = alternativeCandidate
    ? [
        tacticFromCatch(alternativeCandidate.item),
        clean(alternativeCandidate.item.rig)
          ? `Rig: ${clean(alternativeCandidate.item.rig)}`
          : null,
        clean(alternativeCandidate.item.main_bait)
          ? `Main bait: ${clean(alternativeCandidate.item.main_bait)}`
          : null,
        clean(alternativeCandidate.item.hookbait)
          ? `Hookbait: ${clean(alternativeCandidate.item.hookbait)}`
          : null,
        legacyAdditiveFromCatch(alternativeCandidate.item)
          ? `Additive: ${legacyAdditiveFromCatch(alternativeCandidate.item)}`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  // ─── Explanations ─────────────────────────────────────────────────────────
  const noEvidence =
    "No comparable historical evidence is recorded for this recommendation yet.";

  // Quality notes for explanation — only mention when they actually applied.
  const qualityNotes: string[] = [];
  if (estimatedCatchCount > 0)
    qualityNotes.push(`${estimatedCatchCount} catch${estimatedCatchCount === 1 ? " with" : "es with"} estimated time excluded from bite-time evidence.`);
  if (
    scoredSessions.some(
      (s) =>
        Number(s.session.catch_count) === 0 &&
        durationWeight(s.session.duration_hours, false) < 0.8,
    )
  )
    qualityNotes.push("Short blank sessions were duration-weighted to reduce their negative exposure.");
  if (
    scoredSessions.some(
      (s) =>
        Number(s.session.catch_count) > 0 &&
        durationWeight(s.session.duration_hours, true) < 1,
    )
  )
    qualityNotes.push("Sub-hour successful sessions were downweighted as limited exposure.");
  if (incidentalCatchCount > 0)
    qualityNotes.push(
      `${incidentalCatchCount} caught-species observation${incidentalCatchCount === 1 ? "" : "s"} from session${incidentalSessions.length === 1 ? "" : "s"} targeting another species contributed reduced positive prediction evidence and informed tactic/location patterns. Those sessions did not enter the species catch-rate or blank denominator.`,
    );
  if (isCrossWater)
    qualityNotes.push("Score based on general CatchCompass evidence from comparable sessions on other waters. Lake-specific knowledge is limited.");

  const scoreExplanation =
    !hasScore
      ? "No comparable angler-sessions or caught-species observations have been recorded yet. Log more sessions, including blanks, to generate a score."
      : evidenceSessionCount === 0 && incidentalCatchCount > 0
        ? `Fishing Score is a prediction score (not the catch rate). ${incidentalCatchCount} caught-species observation${incidentalCatchCount === 1 ? "" : "s"} from session${incidentalSessions.length === 1 ? "" : "s"} targeting another species provide reduced positive evidence because the species was present, feeding and catchable. No species catch rate is calculated until there is deliberately targeted exposure.`
        : `Fishing Score is a prediction score (not the catch rate). ${isCrossWater ? "Cross-water fallback: " : ""}${evidenceSessionCount} comparable angler-session${evidenceSessionCount === 1 ? "" : "s"} from ${evidencePhrase}. Historical catch rate: ${historicalCatchRatePct}% (${evidenceSessionCount - evidenceBlankSessionCount} successful, ${evidenceBlankSessionCount} blank).${qualityNotes.length ? " " + qualityNotes.join(" ") : ""}`;

  const confidenceExplanation = isCrossWater
    ? `Low Confidence (cross-water). Based on ${evidenceSessionCount} comparable session${evidenceSessionCount === 1 ? "" : "s"} on other waters — no lake-specific evidence is currently available. Add a session at this lake to build targeted advice.`
    : evidenceSessionCount === 0
      ? incidentalCatchCount > 0
        ? `Low Confidence. CatchCompass has positive caught-species evidence from this lake, but no deliberately targeted session exposure yet. Incidental catches can nudge the prediction, while sessions targeting something else with no such catch remain neutral rather than becoming blanks.`
        : "No comparable angler-sessions recorded. Score and recommendations will appear once you have logged real sessions."
      : scoreEvidenceStrength < 25
        ? `Low Confidence from a small body of relevant evidence. Recommendations are directional only.`
        : scoreEvidenceStrength < 100
          ? `Medium Confidence from the available comparable evidence.`
          : `High Confidence from the available comparable evidence.`;

  const crossWaterSpatialExplanation =
    "Comparable observations exist on other waters, but CatchCompass does not transfer peg, depth or distance evidence between lakes.";
  const crossWaterTimeExplanation =
    "Comparable catches exist on other waters, but CatchCompass does not transfer bite-time evidence between lakes.";

  const areaExplanation = isCrossWater
    ? crossWaterSpatialExplanation
    : recommendedArea
      ? `${patternStrengthExplanation("peg/swim", areaPattern) ?? noEvidence} Saved spots within the peg remain context only and are not promoted into the headline Where recommendation.`
      : noEvidence;

  const depthExplanation = isCrossWater
    ? crossWaterSpatialExplanation
    : recommendedDepthFt !== null
      ? `Context only: comparable catches with recorded depth centred around ${Number(recommendedDepthFt.toFixed(1))} ft. CatchCompass does not treat this as a best depth because productive depth can move with season, water level, weed, oxygen and fish behaviour.`
      : noEvidence;

  const distanceExplanation = isCrossWater
    ? crossWaterSpatialExplanation
    : recommendedDistanceYds !== null
      ? `Context only: comparable catches with recorded range centred around ${Number(recommendedDistanceYds.toFixed(1))} yds. CatchCompass does not treat this as a best distance because fish location and feeding range can shift through the year and during a session.`
      : noEvidence;

  const bitePeriodExplanation = isCrossWater
    ? crossWaterTimeExplanation
    : patternStrengthExplanation("bite period", bitePeriodPattern) ??
      "No comparable catch fell inside the planned time window.";

  const rigExplanation =
    patternStrengthExplanation("rig", rigPattern) ?? noEvidence;

  const mainBaitExplanation =
    patternStrengthExplanation("main bait", mainBaitPattern) ?? noEvidence;

  const hookbaitExplanation =
    patternStrengthExplanation("hookbait", hookbaitPattern) ?? noEvidence;

  const additiveExplanation =
    patternStrengthExplanation("attractor/additive", additivePattern) ?? noEvidence;

  const baitAmountExplanation =
    recommendedBaitApproach
      ? `${patternStrengthExplanation("feeding approach", baitApproachPattern) ?? "The feeding approach is supported by comparable catches."} Exact recorded quantities remain context only and are not treated as a best amount.`
      : baitAmountRows.length
        ? `CatchCompass has ${baitAmountRows.length} comparable catch${baitAmountRows.length === 1 ? "" : "es"} with recorded feed quantities. Those amounts remain learning context only; there is not enough approach evidence to recommend a fixed feeding level.`
        : noEvidence;

  const tacticExplanation =
    patternStrengthExplanation("tactic", tacticPattern) ??
    "No comparable hookbait or rig text identifies a bottom bait, pop-up, wafter or zig approach yet.";

  const alternativeExplanation = alternativeTactic
    ? `The next strongest distinct observed combination, ranked by independent supporting sessions and condition similarity.`
    : "No second distinct tactic is supported by the available evidence.";

  const explanations: Record<string, string> = {
    score: scoreExplanation,
    confidence: confidenceExplanation,
    area: areaExplanation,
    depth: depthExplanation,
    distance: distanceExplanation,
    bitePeriod: bitePeriodExplanation,
    rig: rigExplanation,
    mainBait: mainBaitExplanation,
    hookbait: hookbaitExplanation,
    additive: additiveExplanation,
    baitAmount: baitAmountExplanation,
    tactic: tacticExplanation,
    alternative: alternativeExplanation,
    patternStatusArea: areaPattern?.status ?? "",
    whyTodayArea: whyThisFitsToday(areaPattern) ?? "",
    patternStatusBitePeriod: bitePeriodPattern?.status ?? "",
    whyTodayBitePeriod: whyThisFitsToday(bitePeriodPattern) ?? "",
    patternStatusRig: rigPattern?.status ?? "",
    whyTodayRig: whyThisFitsToday(rigPattern) ?? "",
    patternStatusMainBait: mainBaitPattern?.status ?? "",
    whyTodayMainBait: whyThisFitsToday(mainBaitPattern) ?? "",
    patternStatusHookbait: hookbaitPattern?.status ?? "",
    whyTodayHookbait: whyThisFitsToday(hookbaitPattern) ?? "",
    patternStatusAdditive: additivePattern?.status ?? "",
    whyTodayAdditive: whyThisFitsToday(additivePattern) ?? "",
    patternStatusBaitApproach: baitApproachPattern?.status ?? "",
    whyTodayBaitApproach: whyThisFitsToday(baitApproachPattern) ?? "",
    patternStatusTactic: tacticPattern?.status ?? "",
    whyTodayTactic: whyThisFitsToday(tacticPattern) ?? "",
  };

  return {
    fishingScore,
    hasScore,
    confidenceLevel,
    historicalCatchRatePct,
    recommendedArea: isCrossWater ? null : recommendedArea,
    areaQualifier: isCrossWater ? null : areaQualifier,
    recommendedDepthFt: isCrossWater ? null : recommendedDepthFt,
    depthQualifier: isCrossWater ? null : depthQualifier,
    recommendedDistanceYds: isCrossWater ? null : recommendedDistanceYds,
    distanceQualifier: isCrossWater ? null : distanceQualifier,
    bestBitePeriod: isCrossWater ? null : bestBitePeriod,
    bitePeriodQualifier: isCrossWater ? null : bitePeriodQualifier,
    recommendedRig,
    rigQualifier,
    recommendedMainBait,
    mainBaitQualifier,
    recommendedHookbait,
    hookbaitQualifier,
    recommendedAdditive,
    additiveQualifier,
    recommendedBaitAmount,
    recommendedBaitUnit,
    recommendedBaitApproach,
    baitApproachQualifier,
    recommendedTactic,
    tacticQualifier,
    alternativeTactic,
    explanations,
    evidenceSessionCount,
    effectiveEvidenceSessionCount,
    evidenceBlankSessionCount,
    evidenceCatchCount,
    personalSessionCount,
    communitySessionCount,
    communityIncluded,
    isCrossWater,
  };
}

// ─── UI helpers ───────────────────────────────────────────────────────────────

export function formatAdvisorValue(
  value: number | null,
  unit: string,
): string | null {
  if (value === null) return null;
  return `${Number(value.toFixed(1))} ${unit}`;
}

/** Returns a human-readable date like "22 Aug 2026" from a YYYY-MM-DD string
 * or an ISO timestamp such as "2026-08-23T00:00:00.000Z".
 * Always treats the value as a calendar date — extracts the YYYY-MM-DD prefix
 * before any T/timezone so the timezone cannot shift it to another day.
 */
export function formatAdvisorDate(date: string | null | undefined): string {
  if (!date) return "Date not set";
  // Extract the date portion only — works for both "2026-08-23" and
  // "2026-08-23T00:00:00.000Z" or any other ISO variant.
  const datePart = String(date).slice(0, 10); // always "YYYY-MM-DD"
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return date;
  // Parse at noon UTC so no timezone can shift to the previous or next day.
  const d = new Date(`${year}-${month}-${day}T12:00:00Z`);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

